import {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useEffect,
} from 'react';

export interface CustomMenuItem {
  value: string;
  label: string;
  onClick: () => void;
}

interface DashletMenuContextValue {
  customMenuItems: CustomMenuItem[];
  setCustomMenuItems: React.Dispatch<React.SetStateAction<CustomMenuItem[]>>;
  canEdit: boolean;
  canModifyDashlet: boolean;
  setCanModifyDashlet: (value: boolean) => void;
}

const defaultContext: DashletMenuContextValue = {
  customMenuItems: [],
  setCustomMenuItems: () => {},
  canEdit: false,
  canModifyDashlet: true, // to stop the dashlet from being edited, or duplicated, users can delete still
  setCanModifyDashlet: () => {},
};

const DashletMenuContext =
  createContext<DashletMenuContextValue>(defaultContext);

interface DashletMenuProviderProps {
  children: ReactNode;
  canEdit: boolean;
}

export const DashletMenuProvider = ({
  children,
  canEdit,
}: DashletMenuProviderProps) => {
  const [customMenuItems, setCustomMenuItems] = useState<CustomMenuItem[]>([]);
  const [canModifyDashlet, setCanModifyDashlet] = useState(true);

  const value = useMemo(
    () => ({
      customMenuItems,
      setCustomMenuItems,
      canEdit,
      canModifyDashlet,
      setCanModifyDashlet,
    }),
    [customMenuItems, setCustomMenuItems, canEdit, canModifyDashlet]
  );

  return (
    <DashletMenuContext.Provider value={value}>
      {children}
    </DashletMenuContext.Provider>
  );
};

export const getStableMenuItems = (menuItems: CustomMenuItem[]) => {
  return menuItems
    .map((item) => item.value)
    .sort()
    .join(',');
};

export const useDashletMenu = (): DashletMenuContextValue => {
  const context = useContext(DashletMenuContext);

  if (!context) {
    return defaultContext;
  }

  return context;
};

export const useCustomMenuItems = (items: CustomMenuItem[]) => {
  const { setCustomMenuItems } = useDashletMenu();

  useEffect(() => {
    setCustomMenuItems((prev) => {
      const prevKey = getStableMenuItems(prev);
      const newKey = getStableMenuItems(items);

      if (prevKey === newKey) {
        return prev;
      }

      return items;
    });
  }, [setCustomMenuItems, items]);
};

export default DashletMenuContext;
